import { UseFormReturn, useWatch } from "react-hook-form";

import { useTranslation } from "../../core/translation.core";
import { LocaleTextAresDefaultLocale, LocalInputDefaultLocale } from "../locale/locale.comp";
import { EditorSection, Title } from "../reuseables/editor.comp";
import { EditingMode, FancyCheckbox, LabelAndInput, LabelAndSelect } from "../reuseables/form.comp";

import { UseMutationResult } from "@tanstack/react-query";
import GenericFormEditor from "../form/genericFormEditor";
import { GiftCardDefFormData } from "../../types/giftCard";
import ProductPrices from "../product/productPrices.comp";
import ProductMediaEditor from "../product/productMedia.comp";
import ProductStatusEditor from "../product/productStatusEditor";
import ExcludePlacementEditor from "../common/excludePlacementEditor.comp";
import useShop from "../../hooks/useShop.hook";
import GiftCardTypeSelector from "./giftCardTypeSelector";
import MultipleMembershipsSelector from "../reuseables/multipleMembershipsSelector";
import MultipleCategoriesSelector from "../reuseables/multipleCategoriesSelector";
import MultipleProductsSelector from "../reuseables/multipleProductsSelector";
import PlacementRestrictionForm from "../shipSetting/placementRestriction.comp";

interface Props {
  btnText: string;
  editingMode: EditingMode;
  mutation: UseMutationResult<void, Error, GiftCardDefFormData, unknown>;
  methods: UseFormReturn<GiftCardDefFormData, any>;
}

export default function GiftCardDefEditor(props: Props) {
  const { t, msg } = useTranslation();
  const { selectedShop } = useShop();
  const methods = props.methods;
  const [issueMode] = useWatch({ control: props.methods.control, name: ["issueMode"] });

  if (!selectedShop) return null;

  return (
    <GenericFormEditor methods={methods} mutation={props.mutation} btnText={props.btnText}>
      <EditorSection>
        <Title>{t(msg.product.generalInfo)}</Title>
        <div className="space-y-2">
          <LocalInputDefaultLocale register={methods.register} fieldName="name" label={t(msg.general.name)} />
          <LabelAndInput
            register={methods.register}
            fieldName="sku"
            label={t(msg.product.sku)}
            labelCss=""
            topLabelCss="block"
            registerOptions={{ required: true }}
            errors={methods.formState.errors}
          />
          <LabelAndInput
            register={methods.register}
            fieldName="validityPeriod"
            label={t(msg.giftCard.validityPeriod)}
            labelCss=""
            topLabelCss="block"
            type="number"
            registerOptions={{ required: true }}
            errors={methods.formState.errors}
          />
          <LabelAndInput
            register={methods.register}
            fieldName="startDate"
            label={t(msg.giftCard.startDate)}
            labelCss=""
            topLabelCss="block"
            type="date"
            errors={methods.formState.errors}
          />
          <div className="flex gap-2">
            <GiftCardTypeSelector methods={methods} />
            <AggregationModeSelector methods={methods} />
          </div>
        </div>
      </EditorSection>

      <EditorSection>
        <Title>{t(msg.product.description)}</Title>
        <LocaleTextAresDefaultLocale register={methods.register} fieldName="description" />
      </EditorSection>
      <EditorSection>
        <Title>{t(msg.product.shortDescription)}</Title>
        <LocalInputDefaultLocale register={methods.register} fieldName="shortDescription" />
      </EditorSection>

      <EditorSection>
        <Title>{t(msg.giftCard.value)}</Title>
        <LabelAndInput
          register={methods.register}
          fieldName="value"
          label=""
          labelCss=""
          topLabelCss="block"
          type="number"
          registerOptions={{ required: true }}
          errors={methods.formState.errors}
        />
      </EditorSection>

      <EditorSection>
        <Title>{t(msg.product.price2)}</Title>
        <ProductPrices methods={methods} editingMode={props.editingMode} />
      </EditorSection>

      <EditorSection>
        <Title>{t(msg.membership.issueCriteria)}</Title>
        <div>
          <LabelAndSelect
            methods={methods}
            fieldName="issueMode"
            topLabelCss="block"
            label={t(msg.membership.activationMethod)}
            setDefault={false}
            options={[
              { label: t(msg.product.auto), value: "auto" },
              { label: t(msg.product.manual), value: "manual" },
            ]}
          />
          {issueMode === "auto" ? <AutoIssueCriteria methods={methods} /> : null}
        </div>
      </EditorSection>

      <EditorSection>
        <Title>{t(msg.coupon.eligibility)}</Title>
        <div className="flex flex-col gap-5">
          <LabelAndSelect
            methods={methods}
            fieldName="eligibility.placement"
            registerOptions={{ required: true }}
            label={t(msg.coupon.placement)}
            topLabelCss="block"
            options={[
              { label: t(msg.placement.eshop), value: "online" },
              { label: t(msg.placement.retail), value: "pos" },
              { label: t(msg.placement.both), value: "both" },
            ]}
          />

          <LabelAndInput
            register={methods.register}
            fieldName="eligibility.orderOverAmt"
            label={t(msg.coupon.minOrderAmount)}
            topLabelCss="block"
            type="number"
          />

          <FancyCheckbox value={true} register={methods.register("eligibility.withOtherGiftCards")}>
            {t(msg.coupon.combineWithOtherCoupon)}
          </FancyCheckbox>

          <FancyCheckbox value={true} register={methods.register("eligibility.autoApply")}>
            {t(msg.coupon.autoApply)}
          </FancyCheckbox>

          <div className="flex flex-col gap-2">
            <span className="block text-xs font-semibold">{t(msg.coupon.includeMemberships)}</span>
            <MultipleMembershipsSelector
              methods={methods}
              fieldName="eligibility.includeMembershipIds"
              buttonText={t(msg.coupon.addIncludeMemberships)}
              btnId="add-include-memberships-btn"
            />
          </div>
          <div className="flex flex-col gap-2">
            <span className="block text-xs font-semibold">{t(msg.coupon.excludeMemberships)}</span>
            <MultipleMembershipsSelector
              methods={methods}
              fieldName="eligibility.excludeMembershipIds"
              buttonText={t(msg.coupon.addExcludeMemberships)}
              btnId="add-exclude-memberships-btn"
            />
          </div>
          <div className="flex flex-col gap-2">
            <span className="block text-xs font-semibold">{t(msg.giftCard.excludeCategories)}</span>
            <MultipleCategoriesSelector
              methods={methods}
              fieldName="eligibility.excludeCategoryIds"
              buttonText={t(msg.category.exclude)}
              btnId="add-exclude-categories-btn"
            />
          </div>
          <div className="flex flex-col gap-2">
            <span className="block text-xs font-semibold">{t(msg.giftCard.excludeProducts)}</span>
            <MultipleProductsSelector
              methods={methods}
              fieldName="eligibility.excludeProductIds"
              buttonText={t(msg.product.exclude)}
              btnId="add-exclude-products-btn"
              showVariants={true}
            />
          </div>
          <div className="flex flex-col gap-2">
            <span className="block text-xs font-semibold">{t(msg.coupon.includeCategories)}</span>
            <MultipleCategoriesSelector
              methods={methods}
              fieldName="eligibility.attachCategoryIds"
              buttonText={t(msg.category.add)}
              btnId="add-include-categories-btn"
            />
          </div>
          <div className="flex flex-col gap-2">
            <span className="block text-xs font-semibold">{t(msg.coupon.includeProducts)}</span>
            <MultipleProductsSelector
              methods={methods}
              fieldName="eligibility.attachProductIds"
              buttonText={t(msg.product.add)}
              btnId="add-include-products-btn"
              showVariants={true}
            />
          </div>
          <PlacementRestrictionForm<GiftCardDefFormData>
            methods={methods}
            typeFieldName="eligibility.placementRestriction.type"
            eshopFieldName="eligibility.placementRestriction.restrictToEshop"
            retailIdsFieldName="eligibility.placementRestriction.restrictToRetailIds"
          />
        </div>
      </EditorSection>

      <EditorSection>
        <Title>{t(msg.product.productVisibility)}</Title>
        <div>
          <ProductStatusEditor methods={methods} />
          <ExcludePlacementEditor methods={methods} />
        </div>
      </EditorSection>
      <EditorSection>
        <Title>{t(msg.reward.reward)}</Title>
        <div className="flex flex-col gap-4">
          <LabelAndInput
            label={t(msg.shop.rewardPayout)}
            register={methods.register}
            fieldName="rewardPayout"
            topLabelCss="block"
          />

          <FancyCheckbox value={true} register={methods.register("giftCardAutoConversionEnabled")}>
            {t(msg.shop.giftCardAutoConversionEnabled)}
          </FancyCheckbox>

          <FancyCheckbox value={true} register={methods.register("isRewardRedeemable")}>
            {t(msg.reward.isRewardRedeemable)}
          </FancyCheckbox>
        </div>
      </EditorSection>

      <EditorSection>
        <Title>{t(msg.media.media)}</Title>
        <ProductMediaEditor methods={methods} />
      </EditorSection>
    </GenericFormEditor>
  );
}

function AutoIssueCriteria({ methods }: { methods: UseFormReturn<GiftCardDefFormData> }) {
  const { t, msg } = useTranslation();
  return (
    <div className="flex flex-col gap-2">
      {/* <Selector
        methods={methods}
        fieldName="autoIssueCriteria.placement"
        label="Placement"
        options={[
          { name: t(msg.placement.eshop), value: "eshop" },
          { name: t(msg.placement.retail), value: "retail" },
          { name: t(msg.placement.both), value: "both" },
        ]}
      /> //TODO  */}
      <LabelAndInput
        step="0.01"
        register={methods.register}
        fieldName="autoIssueCriteria.minTriggerAmt"
        label={t(msg.membership.minTriggerAmount)}
        labelCss=""
        topLabelCss="block"
        type="number"
        errors={methods.formState.errors}
        registerOptions={{ required: true }}
      />

      <FancyCheckbox value={true} register={methods.register("autoIssueCriteria.firstTimeReg")}>
        {t(msg.membership.firstTimeIssue)}
      </FancyCheckbox>
    </div>
  );
}

function AggregationModeSelector({ methods }: { methods: UseFormReturn<GiftCardDefFormData> }) {
  const { t, msg } = useTranslation();
  return (
    <LabelAndSelect
      methods={methods}
      fieldName="aggregationMode"
      topLabelCss="block"
      label={t(msg.giftCard.aggregationMode)}
      setDefault={false}
      options={[
        { label: t(msg.giftCard.noAggregation), value: "none" },
        { label: t(msg.giftCard.allowAggregation), value: "allow" },
      ]}
    />
  );
}
